import { Stack, Text, Paper, Group, Progress } from '@mantine/core';
import { IconCalendar, IconClock } from '@tabler/icons-react';
import Dates from 'components/Dates';
import React from 'react';

interface PlanScheduleSectionProps {
  planningStart: string;
  planningEnd: string;
}

const PlanScheduleSection: React.FC<PlanScheduleSectionProps> = ({
  planningStart,
  planningEnd
}) => {
  const startDate = new Date(planningStart);
  const endDate = new Date(planningEnd);
  const now = new Date();

  // Calculate progress
  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsed = now.getTime() - startDate.getTime();
  const progress = Math.max(0, Math.min(100, (elapsed / totalDuration) * 100));

  // Calculate days
  const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24));
  const remainingDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  const getStatusColor = () => {
    if (now < startDate) return 'blue';
    if (now >= startDate && now <= endDate) return 'green';
    return 'red';
  };

  return (
    <Paper withBorder p={16} radius='lg'>
      <Stack gap={16}>
        <Group gap={8} align='center'>
          <IconCalendar size={18} />
          <Text fw={600} size='md'>
            Schedule Information
          </Text>
        </Group>

        <Stack gap={12}>
          <div>
            <Text size='xs' c='dimmed' mb={4}>
              Start Date
            </Text>
            <Text size='sm' fw={500}>
              <Dates date={planningStart} dateFormat='dateNameTime' />
            </Text>
          </div>

          <div>
            <Text size='xs' c='dimmed' mb={4}>
              End Date
            </Text>
            <Text size='sm' fw={500}>
              <Dates date={planningEnd} dateFormat='dateNameTime' />
            </Text>
          </div>

          <div>
            <Text size='xs' c='dimmed' mb={4}>
              Duration
            </Text>
            <Group gap={4}>
              <IconClock size={14} />
              <Text size='sm'>{totalDays} days</Text>
            </Group>
          </div>

          {now >= startDate && now <= endDate && (
            <div>
              <Group justify='space-between' mb={4}>
                <Text size='xs' c='dimmed'>
                  Progress
                </Text>
                <Text size='xs' c='dimmed'>
                  {Math.round(progress)}%
                </Text>
              </Group>
              <Progress value={progress} color={getStatusColor()} size='sm' />
              <Text size='xs' c='dimmed' mt={4}>
                {remainingDays > 0 ? `${remainingDays} days remaining` : 'Plan completed'}
              </Text>
            </div>
          )}

          {now < startDate && (
            <div>
              <Text size='xs' c='dimmed' mb={4}>
                Starts in
              </Text>
              <Text size='sm' fw={500} c='blue'>
                {Math.ceil((startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))} days
              </Text>
            </div>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default PlanScheduleSection;
